import React, { useEffect, useRef } from 'react';
import { CheckCircle } from 'lucide-react';

const WhyNuraSection = ({ onBookClick }) => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-up').forEach((el, index) => {
              setTimeout(() => { el.classList.add('visible'); }, index * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const benefits = [
    'No waiting rooms or queues',
    'All pathology request forms accepted',
    'Results sent directly to your referring doctor',
    'NPAAC-certified specimen handling and transport',
  ];

  return (
    /* Band 4: off-white — different from white Services above */
    <section
      id="why-nura"
      ref={sectionRef}
      className="py-20 md:py-32 relative overflow-hidden"
      style={{ background: '#F7F9FB' }}
    >
      {/* Subtle mint glow behind image side */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 80% 50%, rgba(128,229,203,0.07) 0%, transparent 60%)',
        }}
      />

      <div className="container relative z-10">
        <div className="grid md:grid-cols-2 gap-14 items-center max-w-6xl mx-auto">

          {/* Left: Text */}
          <div>
            <div className="fade-up">
              <span className="pill-label">Why Choose Us</span>
              <h2 className="text-[#00001E] mt-2 mb-5">
                Healthcare, delivered differently
              </h2>
              <p className="text-[#2B3879] text-lg mb-8 leading-relaxed">
                We bring professional pathology collection to your door. No waiting rooms,
                no parking stress, no clinic queues. Just qualified care at your convenience.
              </p>
            </div>

            {/* Benefits — mint ticks */}
            <div className="space-y-4 mb-8">
              {benefits.map((benefit) => (
                <div key={benefit} className="fade-up flex items-start gap-4">
                  <div
                    className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(128,229,203,0.20)', border: '1px solid rgba(128,229,203,0.50)' }}
                  >
                    <CheckCircle className="w-4 h-4" style={{ color: '#00001E' }} />
                  </div>
                  <p
                    className="font-medium text-base"
                    style={{ color: '#00001E', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}
                  >
                    {benefit}
                  </p>
                </div>
              ))}
            </div>

            <div className="fade-up">
              <button
                onClick={onBookClick}
                style={{
                  background: '#80E5CB',
                  color: '#00001E',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '14px 30px',
                  fontWeight: '700',
                  fontSize: '15px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 16px rgba(128,229,203,0.35)',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 10px 32px rgba(128,229,203,0.50)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 16px rgba(128,229,203,0.35)';
                }}
              >
                Book your collection today
              </button>
            </div>
          </div>

          {/* Right: Image — white card lifts off the off-white bg */}
          <div className="fade-up">
            <div
              className="relative rounded-2xl overflow-hidden h-96 md:h-[500px]"
              style={{
                boxShadow: '0 16px 56px rgba(43,56,121,0.14), 0 4px 16px rgba(43,56,121,0.08)',
                border: '1px solid rgba(43,56,121,0.08)',
              }}
            >
              <img
                src="https://images.pexels.com/photos/18459702/pexels-photo-18459702.jpeg?auto=compress&cs=tinysrgb&w=940"
                alt="Nura Health qualified pathology collector performing mobile blood collection"
                className="w-full h-full object-cover"
                style={{ filter: 'brightness(0.94)' }}
              />
              {/* Subtle gradient overlay */}
              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(43,56,121,0.08) 0%, rgba(0,0,30,0.15) 100%)',
                }}
              />
              {/* Mint corner accent */}
              <div
                className="absolute top-0 left-0 w-1 h-24 rounded-bl-lg"
                style={{ background: 'linear-gradient(to bottom, #80E5CB, transparent)' }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyNuraSection;
